import React, { useEffect, useRef, useState } from "react";
import {
  Check,
  CheckCircle2,
  ChevronDown,
  GitBranch,
  Moon,
  MoreHorizontal,
  Paperclip,
  PackageOpen,
  Play,
  Plus,
  Send,
  Settings2,
  Sun,
  Trash2,
  Power,
  Wrench,
  X,
} from "lucide-react";
import cuaLightLogo from "../../assets/provider-icons/cua-light.svg";
import desktopCommanderLogo from "../../assets/provider-icons/desktop-commander.svg";
import blenderLogo from "../../assets/provider-icons/blender.svg";
import brightDataLogo from "../../assets/provider-icons/brightdata.svg";
import basicMemoryLogo from "../../assets/provider-icons/basic-memory-dark.svg";

const ROOT_MESSAGES = [
  { id: "df3dbf03", role: "USR", kind: "user", text: "Hello Windie.", time: "12:45 PM" },
  { id: "74d41571", role: "AST", kind: "assistant", text: "Hello Peter, how are you doing today?", time: "12:45 PM", thinking: true },
];

/**
 * Sanitized projection of the real ae6e3a9c conversation. The UUID prefixes
 * are kept so the shape maps back to the inspector's actual message tree,
 * while reasoning text and credentials never ship to the landing page.
 */
const CONVERSATION_BRANCHES = [
  {
    id: "a191528f",
    user: "Can you control Blender for me?",
    groupId: "group-a191528f",
    toolCount: 3,
    details: ["windie__list_providers", "windie__attach_provider", "blender_mcp__get_scene_info"],
    toolSteps: [
      { assistantId: "a191-list", assistant: "Listing available providers", call: "windie__list_providers()", toolId: "a191-list-result", result: "provider_id, description\\ncua-driver, Operate the local computer through the CUA Driver MCP server.\\nblender-mcp, Inspect and control a local Blender instance through MCP." },
      { assistantId: "a191-attach", assistant: "Attaching blender-mcp", call: 'windie__attach_provider(provider_id="blender-mcp")', toolId: "a191-attach-result", result: "provider attached" },
      { assistantId: "a191-scene", assistant: "Checking the Blender scene", call: "blender_mcp__get_scene_info()", toolId: "a191-scene-result", result: "Blender provider unavailable" },
    ],
    leafId: "4028f38a",
    leaf: "I can control Blender through the blender-mcp provider, but it looks like Blender isn't currently available.",
  },
  {
    id: "bcb67b4b",
    user: "Can you control my computer by clicking around and typing just like i do? search providers",
    groupId: "group-bcb67b4b",
    toolCount: 2,
    details: ["windie__list_providers", "windie__attach_provider"],
    toolSteps: [
      { assistantId: "bcb-list", assistant: "Listing available providers", call: "windie__list_providers()", toolId: "bcb-list-result", result: "provider_id, description\\ncua-driver, Operate the local computer through the CUA Driver MCP server.\\ndesktop-commander, Read, write, and manage files and local processes through Desktop Commander.\\nblender-mcp, Inspect and control a local Blender instance through MCP.\\nbrightdata, Search and access live web data through Bright Data MCP.\\nbasic-memory, Store and search Windie's local user memory through Basic Memory." },
      { assistantId: "bcb-attach", assistant: "Attaching cua driver", call: 'windie__attach_provider(provider_id="cua-driver")', toolId: "bcb-attach-result", result: "provider attached" },
    ],
    leafId: "97a488f0",
    leaf: "Yes, I can control your computer by clicking, typing, and interacting with applications — just like you do.",
  },
  {
    id: "215004e7",
    user: "Can you search the Web for me?",
    groupId: "group-215004e7",
    toolCount: 2,
    details: ["windie__list_providers", "windie__attach_provider"],
    toolSteps: [
      { assistantId: "215-list", assistant: "Listing available providers", call: "windie__list_providers()", toolId: "215-list-result", result: "provider_id, description\\nbrightdata, Search and access live web data through Bright Data MCP." },
      { assistantId: "215-attach", assistant: "Attaching brightdata", call: 'windie__attach_provider(provider_id="brightdata")', toolId: "215-attach-result", result: "provider attached" },
    ],
    leafId: "acb350ca",
    leaf: "I can help you search the web using the Bright Data search engine. What specific query would you like me to search for?",
  },
  {
    id: "2857620e",
    user: "Do you have Persistent memory? search your providers and attach",
    groupId: "group-2857620e",
    toolCount: 2,
    details: ["windie__list_providers", "windie__attach_provider"],
    toolSteps: [
      { assistantId: "285-list", assistant: "Listing available providers", call: "windie__list_providers()", toolId: "285-list-result", result: "provider_id, description\\nbasic-memory, Store and search Windie's local user memory through Basic Memory." },
      { assistantId: "285-attach", assistant: "Attaching basic memory", call: 'windie__attach_provider(provider_id="basic-memory")', toolId: "285-attach-result", result: "provider attached" },
    ],
    leafId: "4259faa9",
    leaf: "Yes, I have persistent memory! I've attached the Basic Memory provider which stores and searches Windie's local user memory.",
  },
  {
    id: "0f99b39d",
    user: "You can obviously code right? search your providers and attach",
    groupId: "group-0f99b39d",
    toolCount: 3,
    details: ["windie__list_providers", "windie__attach_provider", "blender_mcp__get_scene_info"],
    toolSteps: [
      { assistantId: "0f99-list", assistant: "Listing available providers", call: "windie__list_providers()", toolId: "0f99-list-result", result: "provider_id, description\\ndesktop-commander, Read, write, and manage files and local processes through Desktop Commander.\\nblender-mcp, Inspect and control a local Blender instance through MCP." },
      { assistantId: "0f99-attach", assistant: "Attaching desktop-commander", call: 'windie__attach_provider(provider_id="desktop-commander")', toolId: "0f99-attach-result", result: "provider attached" },
      { assistantId: "0f99-scene", assistant: "Checking available tools", call: "blender_mcp__get_scene_info()", toolId: "0f99-scene-result", result: "Blender provider unavailable" },
    ],
    leafId: "d94f25d5",
    leaf: "I can see Blender isn't currently running. Let me check the other available providers to work with.",
  },
];

const TREE_NODES = [
  { id: "df3dbf03", role: "USR", text: "Hello Windie.", x: 464, y: 24, parentId: null },
  { id: "74d41571", role: "AST", text: "Hello Peter, how are you doing today?", x: 464, y: 160, parentId: "df3dbf03" },
  ...CONVERSATION_BRANCHES.flatMap((branch, index) => {
    const x = 24 + index * 220;
    return [
      { id: branch.id, role: "USR", text: branch.user, x, y: 320, parentId: "74d41571" },
      { id: branch.groupId, role: "GROUP", text: `${branch.toolCount} tools`, x, y: 470, parentId: branch.id, toolCount: branch.toolCount, details: branch.details },
      { id: branch.leafId, role: "AST", text: branch.leaf, x, y: 550, parentId: branch.groupId, leaf: true },
    ];
  }),
];

const TREE_NODE_WIDTH = 160;
const TREE_NODE_HEIGHT = 96;

function treeNodesForState(expandedGroups) {
  const rootNodes = TREE_NODES.filter((node) => node.id === "df3dbf03" || node.id === "74d41571");
  const branchNodes = CONVERSATION_BRANCHES.flatMap((branch, index) => {
    const x = 24 + index * 220;
    const expanded = expandedGroups.has(branch.groupId);
    const nodes = [
      { id: branch.id, role: "USR", text: branch.user, x, y: 320, parentId: "74d41571" },
      { id: branch.groupId, role: "GROUP", text: `${branch.toolCount} tools`, x, y: 470, parentId: branch.id, toolCount: branch.toolCount, details: branch.details },
    ];
    let parentId = branch.groupId;
    let nextY = 550;

    if (expanded) {
      branch.toolSteps.forEach((step) => {
        nodes.push({ id: step.assistantId, role: "AST", text: step.assistant, x, y: nextY, parentId, expandedStep: true });
        nodes.push({ id: step.toolId, role: "TOL", text: step.result, x, y: nextY + TREE_NODE_HEIGHT + 24, parentId: step.assistantId, toolCall: step.call, expandedStep: true });
        parentId = step.toolId;
        nextY += (TREE_NODE_HEIGHT * 2) + 24;
      });
    }

    nodes.push({ id: branch.leafId, role: "AST", text: branch.leaf, x, y: nextY, parentId, leaf: true });
    return nodes;
  });

  return [...rootNodes, ...branchNodes];
}

const TREE_PATH = (selectedId) => {
  const branch = CONVERSATION_BRANCHES.find((candidate) => [candidate.id, candidate.groupId, candidate.leafId].includes(selectedId));
  const expandedBranch = CONVERSATION_BRANCHES.find((candidate) => candidate.toolSteps.some((step) => [step.assistantId, step.toolId].includes(selectedId)));
  if (!branch && !expandedBranch) return selectedId === "df3dbf03" ? ["df3dbf03"] : ["df3dbf03", "74d41571"];
  if (!branch) {
    const path = ["df3dbf03", "74d41571", expandedBranch.id, expandedBranch.groupId];
    for (const step of expandedBranch.toolSteps) {
      path.push(step.assistantId, step.toolId);
      if (step.assistantId === selectedId || step.toolId === selectedId) break;
    }
    return path;
  }
  const path = ["df3dbf03", "74d41571", branch.id];
  if (selectedId !== branch.id) {
    path.push(branch.groupId);
    if (selectedId === branch.leafId) {
      branch.toolSteps.forEach((step) => path.push(step.assistantId, step.toolId));
      path.push(branch.leafId);
    }
  }
  return path;
};

const branchForNode = (selectedId) => CONVERSATION_BRANCHES.find((branch) => [branch.id, branch.groupId, branch.leafId, ...branch.toolSteps.flatMap((step) => [step.assistantId, step.toolId])].includes(selectedId));

function messagesForSelectedNode(selectedId, expandedTranscriptGroups) {
  const branch = branchForNode(selectedId);
  if (!branch) return ROOT_MESSAGES;
  const messages = [...ROOT_MESSAGES, { id: branch.id, role: "USR", kind: "user", text: branch.user, time: "12:45 PM", toolCount: branch.toolCount }];
  if (selectedId !== branch.id) {
    messages.push({ id: branch.groupId, kind: "group", toolCount: branch.toolCount, details: branch.details });
  }

  const selectedStepIndex = branch.toolSteps.findIndex((step) => step.assistantId === selectedId || step.toolId === selectedId);
  const transcriptExpanded = expandedTranscriptGroups.has(branch.groupId);
  const expandedStepCount = transcriptExpanded ? (selectedId === branch.leafId ? branch.toolSteps.length : selectedStepIndex + 1) : 0;
  if (expandedStepCount > 0) {
    branch.toolSteps.slice(0, expandedStepCount).forEach((step) => {
      messages.push({ id: step.assistantId, role: "AST", kind: "assistant", text: step.assistant, time: "12:45 PM", thinking: true, toolCall: step.call });
      messages.push({ id: step.toolId, role: "TOL", kind: "tool", text: step.result, time: "12:45 PM", toolCall: step.call });
    });
  }
  if (selectedId === branch.leafId) {
    messages.push({ id: branch.leafId, role: "AST", kind: "assistant", text: branch.leaf, time: "12:45 PM", thinking: true });
  }
  return messages;
}

const INITIAL_TOOLS = [
  { name: "brightdata_search", provider: "Bright Data", enabled: true },
  { name: "desktop_commander", provider: "Desktop Commander", enabled: true },
];

const INITIAL_PROVIDERS = [
  { name: "Bright Data", type: "MCP", installed: true },
  { name: "Desktop Commander", type: "MCP", installed: true },
  { name: "Browser Use", type: "MCP", installed: false },
];

const TOOL_GROUPS = [
  { id: "cua-driver", name: "cua-driver", count: 38, attached: true },
  { id: "desktop-commander", name: "desktop-commander", count: 26, attached: false },
  { id: "blender-mcp", name: "blender-mcp", count: 22, attached: true },
  { id: "brightdata", name: "brightdata", count: 5, attached: true },
  { id: "basic-memory", name: "basic-memory", count: 23, attached: true },
];

const EXTENSIONS = [
  { id: "cua-driver", name: "CUA Driver", description: "Operate the local computer through the CUA Driver MCP server.", icon: cuaLightLogo },
  { id: "desktop-commander", name: "Desktop Commander", description: "Read, write, and manage files and local processes through Desktop Commander.", icon: desktopCommanderLogo },
  { id: "blender-mcp", name: "Blender MCP", description: "Inspect and control a local Blender instance through MCP.", icon: blenderLogo },
  { id: "brightdata", name: "Bright Data", description: "Search and access live web data through Bright Data MCP.", icon: brightDataLogo },
  { id: "basic-memory", name: "Basic Memory", description: "Store and search Windie's local user memory through Basic Memory.", icon: basicMemoryLogo },
];

/**
 * The landing page cannot reach a visitor's local Bifrost gateway, so this is
 * a static snapshot of the provider-qualified IDs used by Windie. The
 * inspector gets the same shape from `/api/models` at runtime.
 */
const MODEL_CATALOG = [
  "openrouter/cohere/north-mini-code:free",
  "openai/gpt-4o-mini",
  "anthropic/claude-sonnet-4-5",
  "google/gemini-2.5-flash",
  "google/gemini-2.5-pro",
];

const LIGHT_THEME = {
  "--preview-bg": "#ffffff",
  "--preview-panel": "#fbfbfb",
  "--preview-muted": "#f1f3f5",
  "--preview-line": "rgba(28, 31, 36, 0.14)",
  "--preview-ink": "#272a2f",
  "--preview-subtle": "#73777f",
  "--preview-accent": "#f5a623",
  "--preview-user": "#e7f2fc",
};

const DARK_THEME = {
  "--preview-bg": "#171819",
  "--preview-panel": "#1f2022",
  "--preview-muted": "#292b2e",
  "--preview-line": "rgba(255, 255, 255, 0.14)",
  "--preview-ink": "#f1f2f4",
  "--preview-subtle": "#a4a6ad",
  "--preview-accent": "#ffad24",
  "--preview-user": "#1e3548",
};

function PreviewButton({ children, className = "", ...props }) {
  return (
    <button
      type="button"
      {...props}
      className={`inline-flex items-center justify-center border border-[color:var(--preview-line)] bg-[color:var(--preview-bg)] px-2.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.16em] text-[color:var(--preview-subtle)] transition-colors hover:border-[color:var(--preview-accent)] hover:text-[color:var(--preview-ink)] ${className}`}
    >
      {children}
    </button>
  );
}

function TopBar({ theme, setTheme, onOpenOverlay, onGate, onToggleTree }) {
  return (
    <div className="flex min-h-11 flex-wrap items-center gap-2 border-b border-[color:var(--preview-line)] bg-[color:var(--preview-bg)] px-3 py-1.5 font-mono text-[10px] text-[color:var(--preview-ink)]">
      <div className="flex items-center gap-2 pr-2 md:border-r md:border-[color:var(--preview-line)]">
        <span className="h-2 w-2 bg-[color:var(--preview-ink)]" />
        <span className="font-semibold tracking-tight">windie</span>
      </div>
      <PreviewButton aria-label="Toggle conversation tree" onClick={onToggleTree} className="px-2">
        <GitBranch size={13} />
      </PreviewButton>
      <button
        type="button"
        onClick={onGate}
        className="flex items-center gap-2 border border-[color:var(--preview-line)] bg-[color:var(--preview-bg)] px-3 py-1.5 text-[color:var(--preview-subtle)] hover:text-[color:var(--preview-ink)]"
      >
        conversation ae6e3a9c <ChevronDown size={13} />
      </button>
      <div className="ml-auto flex items-center gap-1.5">
        <PreviewButton onClick={() => onOpenOverlay("system")}>
          <Settings2 size={12} className="mr-1.5" /> SYSTEM
        </PreviewButton>
        <PreviewButton onClick={() => onOpenOverlay("tools")}>
          <Wrench size={12} className="mr-1.5" /> TOOLS
        </PreviewButton>
        <span className="hidden border-l border-[color:var(--preview-line)] px-2 py-1 text-[color:var(--preview-subtle)] md:inline">
          TOKENS <span className="text-[color:var(--preview-ink)]">15k / 256k</span>
        </span>
        <PreviewButton aria-label="Toggle preview theme" onClick={() => setTheme(theme === "light" ? "dark" : "light")} className="px-2">
          {theme === "light" ? <Moon size={13} /> : <Sun size={13} />}
        </PreviewButton>
      </div>
    </div>
  );
}

function TreePanel({ visible, selectedNode, onSelect, expandedGroups, onToggleGroup, onGate }) {
  const treeScrollRef = useRef(null);
  const treeNodes = treeNodesForState(expandedGroups);
  const nodeById = Object.fromEntries(treeNodes.map((node) => [node.id, node]));
  const treeHeight = Math.max(760, Math.max(...treeNodes.map((node) => node.y + (node.role === "GROUP" ? 30 : TREE_NODE_HEIGHT))) + 40);
  useEffect(() => {
    const scrollElement = treeScrollRef.current;
    const selected = treeNodes.find((node) => node.id === selectedNode);
    if (!visible || !scrollElement || !selected) return undefined;

    const frame = window.requestAnimationFrame(() => {
    const target = selected.x + TREE_NODE_WIDTH / 2 - scrollElement.clientWidth / 2;
      const maxScrollLeft = scrollElement.scrollWidth - scrollElement.clientWidth;
      scrollElement.scrollLeft = Math.max(0, Math.min(target, maxScrollLeft));
    });

    return () => window.cancelAnimationFrame(frame);
  }, [selectedNode, treeNodes, visible]);

  useEffect(() => {
    const scrollElement = treeScrollRef.current;
    if (!visible || !scrollElement) return undefined;

    const routeGestureScrollToPage = (event) => {
      event.preventDefault();
      window.scrollBy({ left: event.deltaX, top: event.deltaY, behavior: "auto" });
    };
    const listenerOptions = { passive: false };
    scrollElement.addEventListener("wheel", routeGestureScrollToPage, listenerOptions);

    return () => {
      scrollElement.removeEventListener("wheel", routeGestureScrollToPage, listenerOptions);
    };
  }, [visible]);

  if (!visible) return null;

  const pathSet = new Set(TREE_PATH(selectedNode));

  return (
    <aside className="flex w-full shrink-0 flex-col border-b border-[color:var(--preview-line)] bg-[color:var(--preview-bg)] lg:h-auto lg:w-[27%] lg:border-b-0 lg:border-r">
      <div ref={treeScrollRef} className="min-h-0 flex-1 overflow-auto overscroll-contain bg-[linear-gradient(to_right,transparent_95%,var(--preview-line)_95%),linear-gradient(to_bottom,transparent_95%,var(--preview-line)_95%)] bg-[length:24px_24px]" style={{ touchAction: "none" }}>
        <div className="relative w-[1140px]" style={{ height: treeHeight }}>
          <svg className="pointer-events-none absolute inset-0" width="1140" height={treeHeight} aria-hidden="true">
            {treeNodes.filter((node) => node.parentId).map((node) => {
              const parent = nodeById[node.parentId];
              if (!parent) return null;
              const active = pathSet.has(node.id) && pathSet.has(parent.id);
              return <path key={`${parent.id}-${node.id}`} d={`M ${parent.x + TREE_NODE_WIDTH / 2} ${parent.y + (parent.role === "GROUP" ? 30 : TREE_NODE_HEIGHT)} C ${parent.x + TREE_NODE_WIDTH / 2} ${(parent.y + node.y) / 2}, ${node.x + TREE_NODE_WIDTH / 2} ${(parent.y + node.y) / 2}, ${node.x + TREE_NODE_WIDTH / 2} ${node.y}`} stroke={active ? "var(--preview-accent)" : "var(--preview-line)"} strokeWidth={active ? "2" : "1.2"} fill="none" />;
            })}
          </svg>
          {treeNodes.map((node) => {
            const onPath = pathSet.has(node.id);
            const selected = selectedNode === node.id;
            if (node.role === "GROUP") {
              const expanded = expandedGroups.has(node.id);
              return <React.Fragment key={node.id}>
                <button type="button" onClick={() => onToggleGroup(node.id)} title={expanded ? "collapse tool execution" : "expand tool execution"} className={`absolute flex items-center justify-center gap-2 font-mono text-[9px] uppercase tracking-[0.16em] text-[color:var(--preview-subtle)] hover:text-[color:var(--preview-ink)]`} style={{ left: node.x, top: node.y, width: TREE_NODE_WIDTH, height: 30 }}>
                  <MoreHorizontal size={16} /> {expanded ? "collapse" : `${node.toolCount} tools`}
                </button>
              </React.Fragment>;
            }
            return <button key={node.id} type="button" onClick={() => onSelect(node.id)} className={`absolute box-border flex flex-col items-start justify-start overflow-hidden border p-2 text-left transition-colors ${selected ? "border-[color:var(--preview-ink)] bg-[color:var(--preview-muted)] shadow-[0_0_0_1px_var(--preview-ink)]" : onPath ? "border-[color:var(--preview-accent)] bg-[color:var(--preview-bg)]" : "border-[color:var(--preview-line)] bg-[color:var(--preview-bg)] hover:border-[color:var(--preview-ink)]"}`} style={{ left: node.x, top: node.y, width: TREE_NODE_WIDTH, height: TREE_NODE_HEIGHT }}>
              <span className={`block font-mono text-[9px] font-semibold tracking-[0.16em] ${node.role === "USR" ? "text-sky-500" : node.role === "TOL" ? "text-emerald-600" : "text-[color:var(--preview-ink)]"}`}>[{node.role}]</span>
              <span className={`mt-1 block w-full line-clamp-5 overflow-hidden font-sans text-[10px] leading-snug ${node.role === "TOL" ? "font-mono text-emerald-600" : "text-[color:var(--preview-ink)]"}`}>{node.text}</span>
            </button>;
          })}
        </div>
      </div>
    </aside>
  );
}

function ExecutionMarker({ message, expanded, onToggle }) {
  return (
    <button type="button" onClick={onToggle} aria-expanded={expanded} className="flex w-full items-center justify-center gap-2 border-b border-[color:var(--preview-line)] bg-[color:var(--preview-bg)] py-4 font-mono text-[9px] uppercase tracking-[0.18em] text-[color:var(--preview-subtle)] hover:text-[color:var(--preview-ink)]">
      <ChevronDown size={13} className={expanded ? "" : "-rotate-90"} /> {expanded ? "collapse" : `${message.toolCount} tools`}
    </button>
  );
}

function MessageRow({ message, selected, onSelect }) {
  return (
    <div
      onClick={onSelect}
      className={`group relative block w-full border-b border-l-4 border-[color:var(--preview-line)] px-5 py-5 text-left transition-colors md:px-7 ${message.kind === "user" ? "bg-[color:var(--preview-user)]" : "bg-[color:var(--preview-bg)]"} ${selected ? "border-l-[color:var(--preview-accent)]" : "border-l-transparent hover:bg-[color:var(--preview-muted)]"}`}
    >
      <div className="flex items-start gap-5">
        <span className={`shrink-0 pt-0.5 font-mono text-[10px] font-semibold tracking-[0.16em] ${message.role === "USR" ? "text-sky-500" : message.role === "TOL" ? "text-emerald-600" : "text-[color:var(--preview-ink)]"}`}>
          [{message.role}]
        </span>
        <div className="min-w-0 flex-1">
          <div className="mb-3 flex flex-wrap items-center gap-3 font-mono text-[9px] uppercase tracking-[0.16em] text-[color:var(--preview-subtle)]">
            {message.thinking ? <span className="text-purple-500">thinking</span> : null}
            {message.toolCount ? <span className="text-[color:var(--preview-accent)]">{message.toolCount}/5</span> : null}
          </div>
          {message.kind === "tool" ? <pre className="whitespace-pre-wrap border border-emerald-500/20 bg-emerald-50/50 p-3 font-mono text-[10px] leading-relaxed text-emerald-600">{message.text}</pre> : <p className="max-w-3xl font-sans text-sm leading-relaxed text-[color:var(--preview-ink)] md:text-[15px]">{message.text}</p>}
          {message.toolCall ? <div className="mt-3 border-l-2 border-emerald-500/70 bg-emerald-50/50 px-3 py-2 font-mono text-[9px] leading-relaxed text-emerald-600"><div className="mb-1 uppercase tracking-[0.16em]">tool_calls · 1</div>{message.toolCall}</div> : null}
        </div>
        <span className="hidden shrink-0 font-mono text-[9px] tracking-[0.12em] text-[color:var(--preview-subtle)] md:block">{message.time}</span>
      </div>
    </div>
  );
}

function ModelPicker({ selectedModel, onSelect }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const filterRef = useRef(null);
  const filteredModels = MODEL_CATALOG.filter((model) =>
    model.toLowerCase().includes(search.trim().toLowerCase())
  );

  useEffect(() => {
    if (!open) return undefined;
    const frame = window.requestAnimationFrame(() => filterRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  return (
    <div className="relative">
      <PreviewButton
        data-testid="preview-model"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="max-w-[440px]"
      >
        MODEL&nbsp; <span className="min-w-0 truncate normal-case tracking-normal">{selectedModel}</span> <ChevronDown size={12} className="ml-1 shrink-0" />
      </PreviewButton>
      {open ? (
        <>
          <button
            type="button"
            aria-label="Close model picker"
            className="fixed inset-0 z-10 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div className="absolute bottom-full left-0 z-20 mb-1 w-[420px] max-w-[calc(100vw-3rem)] border border-[color:var(--preview-line)] bg-[color:var(--preview-bg)] shadow-lg">
            <div className="flex items-center justify-between gap-3 border-b border-[color:var(--preview-line)] p-2">
              <input
                ref={filterRef}
                data-testid="preview-model-filter"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="search model"
                aria-label="Search models"
                className="h-8 min-w-0 flex-1 border border-[color:var(--preview-line)] bg-[color:var(--preview-panel)] px-2 font-mono text-xs text-[color:var(--preview-ink)] outline-none placeholder:text-[color:var(--preview-subtle)] focus:border-[color:var(--preview-accent)]"
              />
              <span className="shrink-0 font-mono text-[9px] uppercase tracking-[0.14em] text-[color:var(--preview-subtle)]">{filteredModels.length}/{MODEL_CATALOG.length}</span>
            </div>
            <div className="max-h-[260px] overflow-y-auto py-1">
              {filteredModels.length === 0 ? (
                <div className="px-2.5 py-2 font-mono text-xs text-[color:var(--preview-subtle)]">no models</div>
              ) : filteredModels.map((model) => (
                <button
                  key={model}
                  type="button"
                  data-testid={`preview-model-option-${model}`}
                  onClick={() => {
                    onSelect(model);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center justify-between gap-3 px-2.5 py-1.5 text-left font-mono text-xs hover:bg-[color:var(--preview-muted)] ${selectedModel === model ? "bg-[color:var(--preview-muted)]" : ""}`}
                >
                  <span className="min-w-0 truncate text-[color:var(--preview-ink)]">{model}</span>
                  <span className="shrink-0 text-[10px] uppercase text-[color:var(--preview-subtle)]">{model.split("/")[0]}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

function Composer({ text, setText, attachments, onAttach, onGate, selectedModel, onSelectModel }) {
  const fileRef = useRef(null);
  const submit = (event) => {
    event.preventDefault();
    onGate();
  };

  return (
    <form onSubmit={submit} className="shrink-0 border-t border-[color:var(--preview-line)] bg-[color:var(--preview-bg)] p-3 md:p-4">
      <textarea
        value={text}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            onGate();
          }
        }}
        rows={2}
        placeholder="query the runtime. enter to send, shift + enter for newline."
        className="mb-3 w-full resize-none bg-transparent font-mono text-[11px] text-[color:var(--preview-ink)] outline-none placeholder:text-[color:var(--preview-subtle)]"
      />
      {attachments.length ? (
        <div className="mb-3 flex flex-wrap gap-2">
          {attachments.map((file) => <span key={file} className="inline-flex items-center gap-1 border border-[color:var(--preview-line)] px-2 py-1 font-mono text-[9px] text-[color:var(--preview-subtle)]"><Paperclip size={11} /> {file}</span>)}
        </div>
      ) : null}
      <input ref={fileRef} type="file" className="hidden" onChange={(event) => { if (event.target.files?.[0]) onAttach(event.target.files[0].name); }} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          <PreviewButton onClick={() => fileRef.current?.click()}><Paperclip size={12} className="mr-1.5" /> attach image</PreviewButton>
          <ModelPicker selectedModel={selectedModel} onSelect={onSelectModel} />
          <PreviewButton onClick={onGate}>new session <ChevronDown size={12} className="ml-1" /></PreviewButton>
        </div>
        <div className="flex gap-2">
          <PreviewButton onClick={onGate}><Play size={12} className="mr-1.5" /> continue</PreviewButton>
          <PreviewButton onClick={onGate} className="border-[color:var(--preview-accent)] text-[color:var(--preview-ink)]"><Send size={12} className="mr-1.5" /> query</PreviewButton>
        </div>
      </div>
    </form>
  );
}

function SystemOverlay({ prompt, setPrompt, onClose, onSave }) {
  return (
    <div className="absolute inset-0 z-20 flex items-start justify-center overflow-y-auto bg-[color:var(--preview-bg)]/90 px-6 pb-6 pt-12 backdrop-blur-sm">
      <div className="flex h-[min(586px,calc(100vh-10rem))] max-h-[calc(100vh-10rem)] w-full max-w-4xl flex-col border border-[color:var(--preview-line)] bg-[color:var(--preview-bg)] shadow-lg">
        <div className="flex h-10 shrink-0 items-center justify-between border-b border-[color:var(--preview-line)] px-4">
          <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[color:var(--preview-subtle)]">system prompt</span>
          <button type="button" onClick={onClose} aria-label="Close system prompt" className="p-1 text-[color:var(--preview-subtle)] hover:text-[color:var(--preview-ink)]"><X size={14} strokeWidth={1.75} /></button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-4 p-6">
          <textarea value={prompt} onChange={(event) => setPrompt(event.target.value)} placeholder="Write the system prompt..." className="min-h-0 w-full flex-1 resize-none border border-[color:var(--preview-line)] bg-transparent p-4 font-mono text-sm leading-relaxed text-[color:var(--preview-ink)] outline-none focus:border-[color:var(--preview-ink)]" />
          <div className="flex items-center justify-between"><span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[color:var(--preview-subtle)]">conversation-wide</span><PreviewButton onClick={onSave} className="border-[color:var(--preview-ink)] bg-[color:var(--preview-ink)] px-3 py-1.5 text-white hover:border-[color:var(--preview-ink)] hover:bg-[color:var(--preview-ink)]">save</PreviewButton></div>
        </div>
      </div>
    </div>
  );
}

function ExtensionsPreview() {
  const [catalog, setCatalog] = useState("mcps");
  const [states, setStates] = useState(() => Object.fromEntries(EXTENSIONS.map((extension) => [extension.id, "enabled"])));
  const catalogItems = [{ id: "mcps", label: "mcps", count: 5 }, { id: "skills", label: "skills", count: 0 }, { id: "plugins", label: "plugins", count: 0 }];
  const enabledCount = Object.values(states).filter((state) => state === "enabled").length;
  const toggleState = (id) => setStates((current) => ({ ...current, [id]: current[id] === "enabled" ? "disabled" : "enabled" }));
  const removeExtension = (id) => setStates((current) => ({ ...current, [id]: "removed" }));
  const repairExtension = (id) => setStates((current) => ({ ...current, [id]: "enabled" }));

  return (
    <div className="flex min-h-full flex-col">
      <div className="border-b border-[color:var(--preview-line)] bg-[color:var(--preview-muted)]/25 px-5 py-5">
        <div className="flex items-start gap-3">
          <div className="grid size-10 shrink-0 place-items-center border border-[color:var(--preview-accent)]/50 bg-[color:var(--preview-accent)]/10 text-[color:var(--preview-accent)]"><PackageOpen size={20} strokeWidth={1.5} /></div>
          <div className="min-w-0"><div className="font-sans text-lg font-medium tracking-tight text-[color:var(--preview-ink)]">Extension library</div><p className="mt-1 max-w-2xl text-[12px] leading-relaxed text-[color:var(--preview-subtle)]">Browse MCPs, skills, and plugins available to Windie.</p></div>
          <div className="ml-auto hidden shrink-0 text-right sm:block"><div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[color:var(--preview-subtle)]">{catalog} catalog</div><div className="mt-1 font-mono text-sm text-[color:var(--preview-ink)]">{catalogItems.find((item) => item.id === catalog)?.count || 0} {catalog}</div></div>
        </div>
      </div>
      <div className="flex-1 p-5">
        <div className="mb-5 flex items-center gap-1 border-b border-[color:var(--preview-line)] pb-3">{catalogItems.map((item) => <button type="button" key={item.id} onClick={() => setCatalog(item.id)} className={`h-8 px-3 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${catalog === item.id ? "bg-[color:var(--preview-ink)] text-[color:var(--preview-bg)]" : "text-[color:var(--preview-subtle)] hover:bg-[color:var(--preview-muted)] hover:text-[color:var(--preview-ink)]"}`}>{item.label} <span className="ml-1.5 opacity-60">{item.count}</span></button>)}</div>
        {catalog !== "mcps" ? <div className="flex min-h-48 flex-col items-center justify-center gap-2 text-center"><PackageOpen size={28} className="text-[color:var(--preview-subtle)]" strokeWidth={1.25} /><div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[color:var(--preview-subtle)]">no {catalog} installed</div><p className="max-w-sm text-[12px] text-[color:var(--preview-subtle)]">This catalog is ready for future {catalog} packages.</p></div> : <div className="grid grid-cols-1 gap-3 md:grid-cols-2">{EXTENSIONS.map((extension) => { const state = states[extension.id]; const installed = state !== "removed"; return <article key={extension.id} className="group flex min-h-[250px] flex-col border border-[color:var(--preview-line)] bg-[color:var(--preview-panel)] transition-colors hover:border-[color:var(--preview-subtle)]">
          <div className="flex items-start gap-3 border-b border-[color:var(--preview-line)] p-4"><div className="grid size-12 shrink-0 place-items-center overflow-hidden border border-[color:var(--preview-line)] bg-[color:var(--preview-muted)] text-[color:var(--preview-ink)]"><img src={extension.icon} alt="" aria-hidden="true" className="size-10 object-contain" /></div><div className="min-w-0 flex-1"><div className="flex items-start justify-between gap-2"><div className="min-w-0"><h3 className="truncate font-sans text-base font-medium tracking-tight text-[color:var(--preview-ink)]">{extension.name}</h3><p className="mt-0.5 truncate font-mono text-[9px] uppercase tracking-[0.18em] text-[color:var(--preview-subtle)]">{extension.id}</p></div>{installed ? <span className={`inline-flex items-center gap-1 border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.12em] ${state === "enabled" ? "border-emerald-500/30 text-emerald-600" : "border-[color:var(--preview-line)] text-[color:var(--preview-subtle)]"}`}><CheckCircle2 size={12} /> {state}</span> : null}</div></div></div>
          <div className="flex flex-1 flex-col gap-4 p-4"><p className="min-h-[42px] text-[12px] leading-relaxed text-[color:var(--preview-subtle)]">{extension.description}</p><div className="mt-auto flex items-center justify-between gap-3 border-t border-[color:var(--preview-line)] pt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[color:var(--preview-subtle)]"><span>mcp</span><span>GitHub repository ↗</span></div></div>
          <div className="flex min-h-12 items-center gap-2 border-t border-[color:var(--preview-line)] bg-[color:var(--preview-muted)]/25 px-4 py-2">{installed ? <button type="button" onClick={() => toggleState(extension.id)} className={`inline-flex h-8 flex-1 items-center justify-center gap-2 border px-3 font-mono text-[10px] uppercase tracking-[0.18em] ${state === "enabled" ? "border-[color:var(--preview-line)] text-[color:var(--preview-subtle)] hover:bg-[color:var(--preview-muted)]" : "border-[color:var(--preview-ink)] bg-[color:var(--preview-ink)] text-[color:var(--preview-bg)]"}`}><Power size={13} /> {state === "enabled" ? "disable" : "enable"}</button> : <button type="button" onClick={() => repairExtension(extension.id)} className="inline-flex h-8 flex-1 items-center justify-center gap-2 border border-[color:var(--preview-ink)] bg-[color:var(--preview-ink)] px-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[color:var(--preview-bg)]"><Plus size={13} /> install</button>}<button type="button" title="repair extension" onClick={() => repairExtension(extension.id)} className="grid size-8 place-items-center border border-[color:var(--preview-line)] text-[color:var(--preview-subtle)] hover:bg-[color:var(--preview-muted)] hover:text-[color:var(--preview-ink)]"><Wrench size={14} /></button><button type="button" title="remove extension" onClick={() => removeExtension(extension.id)} className="grid size-8 place-items-center border border-[color:var(--preview-line)] text-[color:var(--preview-subtle)] hover:border-red-500/50 hover:bg-red-50 hover:text-red-500"><Trash2 size={14} /></button></div>
        </article>; })}</div>}
      </div>
    </div>
  );
}

function ToolsOverlay({ tools, setTools, onClose }) {
  const [tab, setTab] = useState("installed");
  const [approvalMode, setApprovalMode] = useState("full");
  const [attachedGroups, setAttachedGroups] = useState(() => new Set(TOOL_GROUPS.filter((group) => group.attached).map((group) => group.id)));
  const overlayScrollRef = useRef(null);
  const toggleGroup = (group) => {
    setAttachedGroups((current) => {
      const next = new Set(current);
      if (next.has(group.id)) next.delete(group.id);
      else next.add(group.id);
      return next;
    });
    setTools((current) => current);
  };
  const handleOverlayWheel = (event) => {
    const scrollElement = overlayScrollRef.current;
    if (!scrollElement) return;
    if (scrollElement.scrollHeight <= scrollElement.clientHeight && scrollElement.scrollWidth <= scrollElement.clientWidth) return;
    event.preventDefault();
    event.stopPropagation();
    scrollElement.scrollTop += event.deltaY;
    scrollElement.scrollLeft += event.deltaX;
  };

  useEffect(() => {
    overlayScrollRef.current?.scrollTo({ top: 0, left: 0 });
  }, [tab]);

  return (
    <div className="absolute inset-0 z-20 flex items-start justify-center overflow-y-auto bg-[color:var(--preview-bg)]/90 px-6 pb-6 pt-12 backdrop-blur-sm">
      <div className="flex h-[min(586px,calc(100vh-10rem))] max-h-[calc(100vh-10rem)] w-full max-w-4xl flex-col overflow-hidden border border-[color:var(--preview-line)] bg-[color:var(--preview-bg)] shadow-lg">
        <div className="flex h-10 shrink-0 items-center justify-between border-b border-[color:var(--preview-line)] px-4"><span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[color:var(--preview-subtle)]">tools</span><button type="button" onClick={onClose} aria-label="Close tools" className="p-1 text-[color:var(--preview-subtle)] hover:text-[color:var(--preview-ink)]"><X size={14} strokeWidth={1.75} /></button></div>
        <div className="flex h-10 shrink-0 items-center gap-1 border-b border-[color:var(--preview-line)] bg-[color:var(--preview-muted)]/20 px-3"><button type="button" onClick={() => setTab("installed")} className={`h-7 px-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${tab === "installed" ? "bg-[color:var(--preview-ink)] text-[color:var(--preview-bg)]" : "text-[color:var(--preview-subtle)] hover:bg-[color:var(--preview-muted)] hover:text-[color:var(--preview-ink)]"}`}>installed</button><button type="button" onClick={() => setTab("extensions")} className={`h-7 px-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${tab === "extensions" ? "bg-[color:var(--preview-ink)] text-[color:var(--preview-bg)]" : "text-[color:var(--preview-subtle)] hover:bg-[color:var(--preview-muted)] hover:text-[color:var(--preview-ink)]"}`}>extensions</button><span className="ml-auto font-mono text-[9px] uppercase tracking-[0.18em] text-[color:var(--preview-subtle)]">{tab === "extensions" ? "local capabilities" : "conversation access"}</span></div>
        <div ref={overlayScrollRef} onWheel={handleOverlayWheel} className="min-h-0 flex-1 overflow-y-auto" style={{ scrollbarGutter: "stable", touchAction: "pan-y" }}>
          {tab === "installed" ? <>
            <div className="border-b border-[color:var(--preview-line)] p-4">
              <div className="mb-3 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[color:var(--preview-subtle)]"><ChevronDown size={13} /> approvals · 0</div>
              <div className="flex items-center gap-5 py-1 text-[11px]"><span className="w-24 shrink-0 font-mono uppercase tracking-[0.18em] text-[color:var(--preview-subtle)]">tool access</span><div className="grid grid-cols-2 border border-[color:var(--preview-line)]"><button type="button" onClick={() => setApprovalMode("manual")} className={`h-8 px-4 font-mono text-[10px] uppercase tracking-[0.18em] ${approvalMode === "manual" ? "bg-[color:var(--preview-ink)] text-[color:var(--preview-bg)]" : "text-[color:var(--preview-subtle)] hover:bg-[color:var(--preview-muted)]"}`}>manual</button><button type="button" onClick={() => setApprovalMode("full")} className={`h-8 border-l border-[color:var(--preview-line)] px-4 font-mono text-[10px] uppercase tracking-[0.18em] ${approvalMode === "full" ? "bg-[color:var(--preview-ink)] text-[color:var(--preview-bg)]" : "text-[color:var(--preview-subtle)] hover:bg-[color:var(--preview-muted)]"}`}>full access</button></div></div>
              <div className="mt-3 font-mono text-[11px] text-[color:var(--preview-subtle)]">no pending</div>
            </div>
            <div className="p-4">
              <div className="mb-3 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[color:var(--preview-subtle)]"><ChevronDown size={13} /> tool schemas · 114</div>
              <div className="space-y-2">{TOOL_GROUPS.map((group) => { const attached = attachedGroups.has(group.id); return <div key={group.id} className="flex min-h-14 items-center justify-between gap-3 border border-[color:var(--preview-line)] bg-[color:var(--preview-panel)] px-3"><div><div className="font-mono text-[10px] uppercase tracking-[0.12em] text-[color:var(--preview-ink)]">{group.name}</div><div className="mt-0.5 font-mono text-[10px] text-[color:var(--preview-subtle)]">{group.count} tools</div></div><button type="button" onClick={() => toggleGroup(group)} aria-label={`${attached ? "remove" : "add"} ${group.name} tools`} className={`grid size-8 place-items-center border border-[color:var(--preview-line)] ${attached ? "text-red-500 hover:bg-red-50" : "text-[color:var(--preview-ink)] hover:bg-[color:var(--preview-muted)]"}`}>{attached ? <Trash2 size={14} /> : <Plus size={14} />}</button></div>; })}</div>
            </div>
          </> : <ExtensionsPreview />}
        </div>
      </div>
    </div>
  );
}

export default function RuntimePreview() {
  const [theme, setTheme] = useState("light");
  const [treeVisible, setTreeVisible] = useState(true);
  const [selectedNode, setSelectedNode] = useState("acb350ca");
  const [selectedTranscriptNode, setSelectedTranscriptNode] = useState("acb350ca");
  const [expandedTreeGroups, setExpandedTreeGroups] = useState(new Set());
  const [expandedTranscriptGroups, setExpandedTranscriptGroups] = useState(new Set());
  const [overlay, setOverlay] = useState(null);
  const [gateMessage, setGateMessage] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("You are Windie, a calm local AI runtime. Be concise, inspectable, and explicit about tool use.");
  const [draftPrompt, setDraftPrompt] = useState(systemPrompt);
  const [tools, setTools] = useState(INITIAL_TOOLS);
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [selectedModel, setSelectedModel] = useState(MODEL_CATALOG[0]);

  useEffect(() => {
    if (!gateMessage) return undefined;
    const timer = window.setTimeout(() => setGateMessage(""), 2800);
    return () => window.clearTimeout(timer);
  }, [gateMessage]);

  const gate = () => setGateMessage("install windie to continue");
  const selectTreeNode = (nodeId) => {
    setSelectedNode(nodeId);
    setSelectedTranscriptNode(nodeId);
  };
  const toggleGroup = (setExpandedGroups, groupId) => setExpandedGroups((current) => {
    const next = new Set(current);
    if (next.has(groupId)) next.delete(groupId);
    else next.add(groupId);
    return next;
  });
  const openOverlay = (nextOverlay) => {
    if (nextOverlay === "system") setDraftPrompt(systemPrompt);
    setOverlay(nextOverlay);
  };

  return (
    <div className="relative flex h-auto flex-col overflow-hidden border border-[color:var(--preview-line)] bg-[color:var(--preview-bg)] font-sans shadow-[0_8px_32px_rgba(0,0,0,0.05)] lg:h-[720px]" style={theme === "light" ? LIGHT_THEME : DARK_THEME} data-testid="runtime-preview">
      <TopBar theme={theme} setTheme={setTheme} onOpenOverlay={openOverlay} onGate={gate} onToggleTree={() => setTreeVisible((value) => !value)} />
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <TreePanel visible={treeVisible} selectedNode={selectedNode} onSelect={selectTreeNode} expandedGroups={expandedTreeGroups} onToggleGroup={(groupId) => toggleGroup(setExpandedTreeGroups, groupId)} onGate={gate} />
        <main className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto">
            {messagesForSelectedNode(selectedNode, expandedTranscriptGroups).map((message) => message.kind === "group" ? <ExecutionMarker key={message.id} message={message} expanded={expandedTranscriptGroups.has(message.id)} onToggle={() => toggleGroup(setExpandedTranscriptGroups, message.id)} /> : <MessageRow key={message.id} message={message} selected={selectedTranscriptNode === message.id} onSelect={() => setSelectedTranscriptNode(message.id)} />)}
          </div>
          <Composer text={text} setText={setText} attachments={attachments} onAttach={(name) => setAttachments((current) => [...current, name])} onGate={gate} selectedModel={selectedModel} onSelectModel={setSelectedModel} />
        </main>
      </div>
      {gateMessage ? <div role="status" className="absolute bottom-20 left-1/2 z-30 -translate-x-1/2 border border-[color:var(--preview-accent)] bg-[color:var(--preview-ink)] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.14em] text-[color:var(--preview-bg)] shadow-lg">{gateMessage}</div> : null}
      {overlay === "system" ? <SystemOverlay prompt={draftPrompt} setPrompt={setDraftPrompt} onClose={() => setOverlay(null)} onSave={() => { setSystemPrompt(draftPrompt); setOverlay(null); }} /> : null}
      {overlay === "tools" ? <ToolsOverlay tools={tools} setTools={setTools} onClose={() => setOverlay(null)} /> : null}
    </div>
  );
}
